import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { OrdemCompraDetalhes, OrdemCompraProdutoServico } from "../../domain/models/ordem_compra_detalhes";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { OrdemCompraInformacaoFaturamentoDropDown } from "../../domain/models/ordem_compra_informacao_faturamento";
import { initialOrdemCompraForm, OrdemCompraForm, OrdemCompraSchema } from "../schemas/OrdemCompras_cadastro/ordem_compra";
import { OrdemCompraCentroCustoDropDown } from "../../domain/models/ordem_compra_centro_custo_dropdown";
import { OrdemCompraBancoDropDown } from "../../domain/models/ordem_compra_banco_dropdown";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { AtributoCompra } from "../../../../common/domain/models/atributo_compra";
import { CancelarOrdemCompraForm } from "../schemas/OrdemCompra_cancelar/cancelar_ordem_compra";
import { OrdemCompraCanceladoStatus } from "../../domain/models/ordem_compra_cancelado_status";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { comprasItemTotal, roundComprasMoney } from "../utils/compras_valores";

import dayjs from "dayjs";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { SubcontaDropdown } from "../../../rh/domain/models/subconta_dropdown";

/**
 * useOrdemCompraCancelarViewModel
 */

export const useOrdemCompraCancelarViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getAtributosCompraUseCase,
    },
    rh: {
      getCepUseCase,
    },
    reports: {
      getReportOrdemCompraUseCase,
    },
    compras: {
      getOrdemCompraInformacaoFaturamentoDropDownUseCase,
      getOrdemCompraCentroCustoDropDownUseCase,
      getOrdemCompraBancoDropDownUseCase,
      getOrdemCompraByIdUseCase,
      getInformacaoFaturamentoByIdUseCase,
      updateOrdemCompraUseCase,
      cancelarOrdemCompraUseCase,
      getOrdemCompraCanceladoStatusUseCase,
    },
  } = useDependencyInjection();

  const {
    loadSubContasDropDown,
  } = useDropDownLoader();

  const { pushErrorNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idOrdemCompra, setIdOrdemCompra] = useState<number>();

  const [informacoesFaturamentoDropdown, setInformacoesFaturamentoDropdown] = useState<OrdemCompraInformacaoFaturamentoDropDown[]>();
  const [centroCustoEnderecoDropdown, setCentroCustoEnderecoDropdown] = useState<OrdemCompraCentroCustoDropDown[]>();
  const [bancosDropdown, setBancosDropdown] = useState<OrdemCompraBancoDropDown[]>();
  const [subcontasDropdown, setSubcontasDropdown] = useState<SubcontaDropdown[]>();

  const [atributoCompra, setAtributoCompra] = useState<AtributoCompra>();

  const [ordemCompraCanceladoStatus, setOrdemCompraCanceladoStatus] = useState<OrdemCompraCanceladoStatus>();

  const [ordemCompra, setOrdemCompra] = useState<OrdemCompraDetalhes>();
  const [produtosServicos, setProdutosServicos] = useState<OrdemCompraProdutoServico[]>();

  const [cancelarOrdemCompraModal, setCancelarOrdemCompraModal] = useState(false);

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const hookForm = useForm<OrdemCompraForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(OrdemCompraSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: message => pushErrorNotification('Erro', message),
  });

  const [tipoEntrega] = hookForm.watch(['entrega']);

  const information = useMemo((): {
    subtotal: number;
    imposto: number;
    frete: number;
    desconto: number;
    acrescimo: number;
    total: number;
  } | undefined => {
    if (ordemCompra === undefined) return undefined;

    const subtotal = roundComprasMoney(ordemCompra.produtosServicos.reduce((acc, item) => {
      if (item.quantidade === null || item.valorUnitario === null) return acc;
      return acc + comprasItemTotal(item.quantidade, item.valorUnitario);
    }, 0));

    const imposto = ordemCompra.imposto ?? 0;
    const frete = ordemCompra.valorFrete ?? 0;
    const desconto = ordemCompra.desconto ?? 0;
    const acrescimo = ordemCompra.acrescimo ?? 0;

    const total = roundComprasMoney((subtotal + imposto + frete + acrescimo) - desconto);

    return {
      subtotal,
      imposto,
      frete,
      desconto,
      acrescimo,
      total,
    };
  }, [
    ordemCompra,
  ]);

  const entregaOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Entrega realizada no endereço cadastrado',
      },
      {
        id: 2,
        label: 'Retirada no fornecedor',
      },
    ];
  }, [
  ]);

  const respDescargaOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Cliente',
      },
      {
        id: 2,
        label: 'Fornecedor',
      },
    ];
  }, [
  ]);

  const tipoContaOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Corrente',
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 2,
        label: 'Poupança',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadAtributoCompra = useCallback(async () => {
    try {
      const atributoCompra = await getAtributosCompraUseCase.getAtributosCompra(null);
      setAtributoCompra(atributoCompra ?? undefined);
    } catch (e: unknown) {
      setAtributoCompra(undefined);
      throw new Error('Falha ao buscar atributos de compras!');
    }
  }, [
    getAtributosCompraUseCase
  ]);

  const loadOrdemCompraStatusCancelado = useCallback(async (id?: number) => {
    try {
      const filteredIdOrdemCompra = idOrdemCompra ?? id;
      const results = await getOrdemCompraCanceladoStatusUseCase
        .getOrdemCompraCanceladoStatus(filteredIdOrdemCompra);
      setOrdemCompraCanceladoStatus(results);
    } catch (e: unknown) {
      setOrdemCompraCanceladoStatus(undefined);
      throw new Error('Falha ao buscar informações de compra!');
    }
  }, [
    idOrdemCompra,
    getOrdemCompraCanceladoStatusUseCase,
  ]);

  const loadOrdemCompraInformacoesFaturamentoDropDown = useCallback(async (id?: number) => {
    try {
      const filteredIdOrdemCompra = idOrdemCompra ?? id;
      const results = await getOrdemCompraInformacaoFaturamentoDropDownUseCase
        .getOrdemCompraInformacaoFaturamentoDropDown(filteredIdOrdemCompra);
      setInformacoesFaturamentoDropdown(results);
    } catch (e: unknown) {
      setInformacoesFaturamentoDropdown(undefined);
      throw new Error('Falha ao buscar informações de compra!');
    }
  }, [
    idOrdemCompra,
    getOrdemCompraInformacaoFaturamentoDropDownUseCase,
  ]);

  const loadOrdemCompraCentroCustoDropDown = useCallback(async (id?: number) => {
    try {
      const filteredIdOrdemCompra = idOrdemCompra ?? id;
      const results = await getOrdemCompraCentroCustoDropDownUseCase
        .getOrdemCompraCentroCustoDropDown(filteredIdOrdemCompra);
      setCentroCustoEnderecoDropdown(results);
    } catch (e: unknown) {
      setCentroCustoEnderecoDropdown(undefined);
      throw new Error('Falha ao buscar centro custo!');
    }
  }, [
    idOrdemCompra,
    getOrdemCompraCentroCustoDropDownUseCase,
  ]);

  const loadOrdemCompraBancoDropDown = useCallback(async (id?: number) => {
    try {
      const filteredIdOrdemCompra = idOrdemCompra ?? id;
      const results = await getOrdemCompraBancoDropDownUseCase
        .getOrdemCompraBancoDropDown(filteredIdOrdemCompra);
      setBancosDropdown(results);
    } catch (e: unknown) {
      setBancosDropdown(undefined);
      throw new Error('Falha ao buscar centro custo!');
    }
  }, [
    idOrdemCompra,
    getOrdemCompraBancoDropDownUseCase,
  ]);

  const loadOrdemCompra = useCallback(async (id?: number) => {
    try {
      const filteredIdOrdemCompra = idOrdemCompra ?? id;
      const results = await getOrdemCompraByIdUseCase
        .getOrdemCompraById(
          TelasIds.OrdemCompra_cancelar,
          filteredIdOrdemCompra,
        );

      hookForm.setValue('nroOC', results.nrOrdemCompra !== null
        ? results.nrOrdemCompra.toString() : '');

      hookForm.setValue('dataCadastro', results.dataCadastro !== null
        ? dayjs.utc(results.dataCadastro).local().toDate() : undefined);

      hookForm.setValue('nroColeta', results.nrColetaPreco !== null
        ? results.nrColetaPreco.toString() : '');

      hookForm.setValue('dataOC', results.dataRef !== null
        ? dayjs.utc(results.dataRef).local().toDate() : undefined);

      hookForm.setValue('centroCusto', results.centroCusto ?? undefined);
      hookForm.setValue('subconta', results.idSubconta ?? undefined);
      hookForm.setValue('comprador', results.comprador ?? undefined);
      hookForm.setValue('respAprovacao', results.respAprov ?? undefined);

      hookForm.setValue('dataEntrega', results.dataEntrega !== null
        ? dayjs.utc(results.dataEntrega).local().toDate() : undefined);

      hookForm.setValue('nroCNO', results.numeroCno ?? undefined);

      hookForm.setValue('entrega', results.tipoEntrega !== null ? (results.tipoEntrega === 'E' ? 1 : 2) : 1);
      hookForm.setValue('respDescarga', results.responsavelDescarga !== null ? (results.responsavelDescarga === 'C' ? 1 : 2) : 1);

      hookForm.setValue('informacoesGerais', results.informacoesGerais ?? undefined);
      hookForm.setValue('observacoes', results.observacao ?? undefined);

      hookForm.setValue('for_nomeFantasia', results.fornNomeFantasia ?? undefined);
      hookForm.setValue('for_cnpj', results.fornCnpj ?? undefined);
      hookForm.setValue('for_razaoSocial', results.fornRazaoSocial ?? undefined);
      hookForm.setValue('for_cpf', results.fornCpf ?? undefined);
      hookForm.setValue('for_contato', results.fornContato ?? undefined);
      hookForm.setValue('for_email', results.fornEmail ?? undefined);
      hookForm.setValue('for_incricaoEstadual', results.fornIncricaoEstadual ?? undefined);
      hookForm.setValue('for_telFixo', results.fornTelefoneFixo ?? undefined);
      hookForm.setValue('for_inscricaoMunicipal', results.fornIncricaoMunicipal ?? undefined);
      hookForm.setValue('for_telCelular', results.fornTelefoneCelular ?? undefined);
      hookForm.setValue('for_cep', results.fornCep ?? undefined);
      hookForm.setValue('for_cidade', results.fornCidade ?? undefined);
      hookForm.setValue('for_rua', results.fornRua ?? undefined);
      hookForm.setValue('for_numero', results.fornNumero ?? undefined);
      hookForm.setValue('for_complemento', results.fornApartamento ?? undefined);
      hookForm.setValue('for_bairro', results.fornBairro ?? undefined);
      hookForm.setValue('for_estado', results.fornUf ?? undefined);
      hookForm.setValue('for_pais', results.fornPais ?? undefined);

      hookForm.setValue('informacoesFaturamento', results.fatIdInformacaoFaturamento ?? undefined);
      hookForm.setValue('fat_nomeFantasia', results.fatNomeFantasia ?? undefined);
      hookForm.setValue('fat_cnpj', results.fatCnpj ?? undefined);
      hookForm.setValue('fat_razaoSocial', results.fatRazaoSocial ?? undefined);
      hookForm.setValue('fat_cpf', results.fatCpf ?? undefined);
      hookForm.setValue('fat_contato', results.fatContato ?? undefined);
      hookForm.setValue('fat_email', results.fatEmail ?? undefined);
      hookForm.setValue('fat_incricaoEstadual', results.fatIncricaoEstadual ?? undefined);
      hookForm.setValue('fat_telFixo', results.fatTelefoneFixo ?? undefined);
      hookForm.setValue('fat_inscricaoMunicipal', results.fatIncricaoMunicipal ?? undefined);
      hookForm.setValue('fat_telCelular', results.fatTelefoneCelular ?? undefined);
      hookForm.setValue('fat_cep', results.fatCep ?? undefined);
      hookForm.setValue('fat_cidade', results.fatCidade ?? undefined);
      hookForm.setValue('fat_rua', results.fatRua ?? undefined);
      hookForm.setValue('fat_numero', results.fatNumero ?? undefined);
      hookForm.setValue('fat_complemento', results.fatApartamento ?? undefined);
      hookForm.setValue('fat_bairro', results.fatBairro ?? undefined);
      hookForm.setValue('fat_estado', results.fatUf ?? undefined);
      hookForm.setValue('fat_pais', results.fatPais ?? undefined);

      const cep = results.endCep && results.endCep.length > 0
        ? results.endCep.replaceAll('.', '').replaceAll('-', '')
        : '';

      hookForm.setValue('endereco', results.endIdCentroCustoEndereco ?? undefined);
      hookForm.setValue('ent_cep', cep ?? undefined);
      hookForm.setValue('ent_cidade', results.endCidade ?? undefined);
      hookForm.setValue('ent_rua', results.endRua ?? undefined);
      hookForm.setValue('ent_numero', results.endNumero ?? undefined);
      hookForm.setValue('ent_complemento', results.endApartamento ?? undefined);
      hookForm.setValue('ent_bairro', results.endBairro ?? undefined);
      hookForm.setValue('ent_estado', results.endUf ?? undefined);
      hookForm.setValue('ent_pais', results.endPais ?? undefined);

      hookForm.setValue('ban_tipoConta', results.banTipoConta === 0 ? 1 : 2);
      hookForm.setValue('ban_banco', results.banNomeBanco ?? undefined);
      hookForm.setValue('ban_agencia', results.banAgencia ?? undefined);
      hookForm.setValue('ban_conta', results.banNrConta ?? undefined);
      hookForm.setValue('ban_digito', results.banDigito ?? undefined);
      hookForm.setValue('ban_operacao', results.banOperacao ?? undefined);
      hookForm.setValue('ban_favorecido', results.banFavorecido ?? undefined);
      hookForm.setValue('ban_cpfcnpj', results.banCpfCnpj ?? undefined);
      hookForm.setValue('ban_pix', results.banPix ?? undefined);

      setOrdemCompra(results);
      setProdutosServicos(results.produtosServicos);
    } catch (e: unknown) {
      setOrdemCompra(undefined);
      throw new Error('Falha ao buscar ordem de compra!');
    }
  }, [
    idOrdemCompra,
    hookForm,
    getOrdemCompraByIdUseCase,
  ]);

  const loadInformacaoFaturamento = useCallback(async (idInformacaoFaturamento: number) => {
    try {
      const results = await getInformacaoFaturamentoByIdUseCase
        .getInformacaoFaturamentoById(idInformacaoFaturamento);

      hookForm.setValue('fat_nomeFantasia', results.nomeFantasia ?? undefined);
      hookForm.setValue('fat_cnpj', results.cnpj ?? undefined);
      hookForm.setValue('fat_razaoSocial', results.razaoSocial ?? undefined);
      hookForm.setValue('fat_cpf', results.cpf ?? undefined);
      hookForm.setValue('fat_contato', results.contato ?? undefined);
      hookForm.setValue('fat_email', results.email ?? undefined);
      hookForm.setValue('fat_incricaoEstadual', results.incricaoEstadual ?? undefined);
      hookForm.setValue('fat_telFixo', results.telefoneFixo ?? undefined);
      hookForm.setValue('fat_inscricaoMunicipal', results.incricaoMunicipal ?? undefined);
      hookForm.setValue('fat_telCelular', results.telefoneCelular ?? undefined);
      hookForm.setValue('fat_cep', results.cep ?? undefined);
      hookForm.setValue('fat_cidade', results.cidade ?? undefined);
      hookForm.setValue('fat_rua', results.rua ?? undefined);
      hookForm.setValue('fat_numero', results.numero ?? undefined);
      hookForm.setValue('fat_complemento', results.apartamento ?? undefined);
      hookForm.setValue('fat_bairro', results.bairro ?? undefined);
      hookForm.setValue('fat_estado', results.uf ?? undefined);
      hookForm.setValue('fat_pais', results.pais ?? undefined);

    } catch (e: unknown) {
      throw new Error('Falha ao buscar ordem de compra!');
    }
  }, [
    getInformacaoFaturamentoByIdUseCase,
    hookForm,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {

      const params = new URLSearchParams(location.search);
      const locationId = params.get('id');
      const id = locationId && locationId.length > 0 ? Number(locationId) : undefined;

      if (id === undefined) throw new Error('Falha ao buscar id de ordem de compra!');

      setIdOrdemCompra(id);

      await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

      await loadAtributoCompra();

      await loadOrdemCompraInformacoesFaturamentoDropDown(id);

      await loadOrdemCompraCentroCustoDropDown(id);

      await loadOrdemCompraBancoDropDown(id);

      await loadOrdemCompraStatusCancelado(id);

      await loadOrdemCompra(id);

    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/OrdemCompra_lista_cancelar' });
  }, [
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

      await loadAtributoCompra();

      await loadOrdemCompraInformacoesFaturamentoDropDown();

      await loadOrdemCompraCentroCustoDropDown();

      await loadOrdemCompraBancoDropDown();

      await loadOrdemCompraStatusCancelado();

      await loadOrdemCompra();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadAtributoCompra,
    loadSubContasDropDown,
    loadOrdemCompraInformacoesFaturamentoDropDown,
    loadOrdemCompraCentroCustoDropDown,
    loadOrdemCompraBancoDropDown,
    loadOrdemCompraStatusCancelado,
    loadOrdemCompra,
    pushErrorNotification,
  ]);

  const handleOrdemCompraSubmit = useCallback(async (form: OrdemCompraForm) => {
    if (idOrdemCompra) {
      try {
        setSaving(true);
        setPageSaving(true);

        await updateOrdemCompraUseCase.updateOrdemCompra(
          idOrdemCompra,
          form.informacoesFaturamento,
          form.endereco,
          form.dataOC,
          form.entrega,
          form.respDescarga,
          form.informacoesGerais,
          form.observacoes,
          form.ent_rua,
          form.ent_numero,
          form.ent_complemento,
          form.ent_cep,
          form.ent_estado,
          form.ent_cidade,
          form.ent_bairro,
          form.ent_pais,
          form.ban_banco,
          form.ban_tipoConta,
          form.ban_conta,
          form.ban_digito,
          form.ban_agencia,
          form.ban_operacao,
          form.ban_favorecido,
          form.ban_cpfcnpj,
          form.ban_pix,
        );

        hookForm.reset({ ...initialOrdemCompraForm });

        await loadOrdemCompra();

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao salvar ordem de compra!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idOrdemCompra,
    hookForm,
    updateOrdemCompraUseCase,
    loadOrdemCompra,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleCancelarOrdemCompraOpenState = useCallback((openState: boolean) => {
    setCancelarOrdemCompraModal(openState);
  }, [
  ]);

  const handleCancelarOrdemCompraSubmit = useCallback(async (form: CancelarOrdemCompraForm) => {
    if (idOrdemCompra) {
      try {
        setSaving(true);
        setPageSaving(true);

        await cancelarOrdemCompraUseCase.cancelarOrdemCompra(
          idOrdemCompra,
          form.motivo,
        );

        await loadOrdemCompraStatusCancelado();

        await loadOrdemCompra();

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

        setCancelarOrdemCompraModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao salvar ordem de compra!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idOrdemCompra,
    cancelarOrdemCompraUseCase,
    loadOrdemCompraStatusCancelado,
    loadOrdemCompra,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleReport = useCallback(async () => {
    if (idOrdemCompra) {
      try {
        setLoading(true);

        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportOrdemCompraUseCase.getReportOrdemCompra(idOrdemCompra, requiredMimeType),
        });

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao gerar relatorio de ordem de compra!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idOrdemCompra,
    getReportOrdemCompraUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleInformacaoFaturamentoChange = useCallback(async (idInformacaoFaturamento: number | string | null) => {
    try {
      setLoading(true);

      let id: number | null = null;

      if (typeof idInformacaoFaturamento === 'number') {
        id = idInformacaoFaturamento;
      } else if (typeof idInformacaoFaturamento === 'string') {
        id = Number(idInformacaoFaturamento);
        if (isNaN(id)) {
          id = null;
        }
      }

      if (id !== null) {
        await loadInformacaoFaturamento(id);
      }

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar informações de faturamento!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadInformacaoFaturamento,
    pushErrorNotification,
  ]);

  const handleCentroCustoEnderecoChange = useCallback(async (idCentroCustoEndereco: number | string | null) => {
    try {
      setLoading(true);

      if (idCentroCustoEndereco) {
        if (centroCustoEnderecoDropdown) {
          const selected = centroCustoEnderecoDropdown.find(x => x.idCentroCustoEndereco === idCentroCustoEndereco);
          if (selected) {
            const cep = selected.cep && selected.cep.length > 0 ? selected.cep : '';

            hookForm.setValue('ent_cep', cep.replaceAll('.', '').replaceAll('-', ''));
            hookForm.setValue('ent_cidade', selected.cidade ?? '');
            hookForm.setValue('ent_rua', selected.rua ?? '');
            hookForm.setValue('ent_numero', selected.numero ?? '');
            hookForm.setValue('ent_complemento', selected.apartamento ?? '');
            hookForm.setValue('ent_bairro', selected.bairro ?? '');
            hookForm.setValue('ent_estado', selected.uf ?? '');
            hookForm.setValue('ent_pais', selected.pais ?? '');
          } else {
            hookForm.setValue('ent_cep', '');
            hookForm.setValue('ent_cidade', '');
            hookForm.setValue('ent_rua', '');
            hookForm.setValue('ent_numero', '');
            hookForm.setValue('ent_complemento', '');
            hookForm.setValue('ent_bairro', '');
            hookForm.setValue('ent_estado', '');
            hookForm.setValue('ent_pais', '');
          }
        }
      } else {
        hookForm.setValue('ent_cep', '');
        hookForm.setValue('ent_cidade', '');
        hookForm.setValue('ent_rua', '');
        hookForm.setValue('ent_numero', '');
        hookForm.setValue('ent_complemento', '');
        hookForm.setValue('ent_bairro', '');
        hookForm.setValue('ent_estado', '');
        hookForm.setValue('ent_pais', '');
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar informações de faturamento!'));
    } finally {
      setLoading(false);
    }
  }, [
    hookForm,
    centroCustoEnderecoDropdown,
    pushErrorNotification,
  ]);

  const handleBancoChange = useCallback(async (idBanco: number | string | null) => {
    try {
      setLoading(true);
      if (bancosDropdown) {
        const selected = bancosDropdown.find(x => x.idBanco === idBanco);
        if (selected) {
          hookForm.setValue('ban_tipoConta', selected.tipoConta === 0 ? 1 : 2);
          hookForm.setValue('ban_banco', selected.nomeBanco ?? undefined);
          hookForm.setValue('ban_agencia', selected.agencia ?? undefined);
          hookForm.setValue('ban_conta', selected.nrConta ?? undefined);
          hookForm.setValue('ban_digito', selected.digito ?? undefined);
          hookForm.setValue('ban_operacao', selected.operacao ?? undefined);
          hookForm.setValue('ban_favorecido', selected.favorecido ?? undefined);
          hookForm.setValue('ban_cpfcnpj', selected.cpfCnpj ?? undefined);
          hookForm.setValue('ban_pix', selected.pix ?? undefined);
        }
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar informações de faturamento!'));
    } finally {
      setLoading(false);
    }
  }, [
    hookForm,
    bancosDropdown,
    pushErrorNotification,
  ]);

  const handleEnderecoCepChange = useCallback(async (cep: string) => {
    const maxCepLength = 8;
    if (cep.length === maxCepLength) {
      const response = await getCepUseCase.getCep(cep);
      if (response) {
        hookForm.setValue('ent_cep', cep);
        hookForm.setValue('ent_rua', response.logradouro.toUpperCase());
        hookForm.setValue('ent_cidade', response.cidade.toUpperCase());
        hookForm.setValue('ent_bairro', response.bairro.toUpperCase());
        hookForm.setValue('ent_estado', response.uf.toUpperCase());
        hookForm.setValue('ent_pais', response.pais.toUpperCase());
      }
    }
  }, [
    hookForm,
    getCepUseCase,
  ]);

  const handleOpenChangeHistory = useCallback((idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (idOrdemCompra) {
        changeHistory.handleOpenChangeHistory(idOrdemCompra, LogTipoTabela.ORDEM_COMPRA, true);
      }
    }
  }, [
    idOrdemCompra,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    idOrdemCompra,
    informacoesFaturamentoDropdown: informacoesFaturamentoDropdown ?? [],
    centroCustoEnderecoDropdown: centroCustoEnderecoDropdown ?? [],
    bancosDropdown: bancosDropdown ?? [],
    subcontasDropdown: subcontasDropdown ?? [],
    atributoCompra: atributoCompra ?? null,
    ordemCompraCanceladoStatus: ordemCompraCanceladoStatus ?? null,
    ordemCompra: ordemCompra ?? null,
    produtosServicos: produtosServicos ?? [],
    cancelarOrdemCompraModal,
    reportViewerState,
    information,
    hookForm,
    changeHistory,
    tipoEntrega,
    entregaOptions,
    respDescargaOptions,
    tipoContaOptions,
    handleBack,
    handleReload,
    handleOrdemCompraSubmit: hookForm.handleSubmit(handleOrdemCompraSubmit),
    handleCancelarOrdemCompraOpenState,
    handleCancelarOrdemCompraSubmit,
    handleReport,
    handleInformacaoFaturamentoChange,
    handleCentroCustoEnderecoChange,
    handleBancoChange,
    handleEnderecoCepChange,
    handleOpenChangeHistory,
  };
}
